import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, tap, throwError } from 'rxjs';
import { AuthTokenService } from '../services/authtoken.service';
import { SessionLogoutService } from '../services/session-logout.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // inject() SIEMPRE al nivel de la función, nunca dentro de pipe()
  const authTokenService = inject(AuthTokenService);
  const router = inject(Router);
  const sessionLogout = inject(SessionLogoutService);
  const token = authTokenService.getToken();

  // 1. Agregar Authorization header solo si hay token válido. withCredentials siempre,
  //    para que la cookie portal-jwt viaje (login, refresh via Set-Cookie, edge-validate via nginx).
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: true })
    : req.clone({ withCredentials: true });

  const startedAt = performance.now();

  return next(authReq).pipe(

    // 2. Renovación de token via header X-New-Token
    tap(event => {
      if (event instanceof HttpResponse) {
        const newToken = event.headers.get('X-New-Token');
        if (newToken) {
          authTokenService.setToken(newToken);
        }
      }
    }),

    // 3. Unwrap de respuesta con formato { data: ... }
    map(event => {
      if (event instanceof HttpResponse) {
        const body = event.body as any;
        if (body && typeof body === 'object' && 'data' in body) {
          return event.clone({ body: body.data });
        }
      }
      return event;
    }),

    // 4. Manejo de errores con acciones reales
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        switch (err.status) {
          case 0:
            // Sin conexión / CORS
            router.navigate(['/error'], {
              queryParams: { message: 'Sin conexión con el servidor', severity: 'error' }
            });
            break;
          case 401:
            // Token expirado / sesion perdida -> mismo flujo que el boton "Cerrar sesion":
            // revoca, limpia, redirige a CIAM logout (termina tambien el SSO).
            sessionLogout.performLogout();
            break;
          case 403:
            if (authTokenService.hasToken()) {
              // Token invalido/sesion perdida -> mismo flujo que "Cerrar sesion".
              sessionLogout.performLogout();
            } else {
              // Sin permisos para este recurso (no es sesion perdida)
              router.navigate(['/error'], {
                queryParams: { message: 'No tienes permisos para acceder a este recurso', severity: 'warning' }
              });
            }
            break;
          case 404:
            // No encontrado — se deja pasar al caller para que decida
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            router.navigate(['/error'], {
              queryParams: { message: 'Error interno del servidor. Intenta más tarde.', severity: 'error' }
            });
            break;
        }
      }
      return throwError(() => err);
    }),

    // 5. Timing log para debugging
    finalize(() => {
      const ms = Math.round(performance.now() - startedAt);
      console.debug(`[HTTP] ${req.method} ${req.urlWithParams} — ${ms}ms`);
    })
  );
};
